import { FC, useCallback, useEffect, useState, useRef } from "react";
import { Box, IconButton, Paper, Typography, LinearProgress, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { NotificationEntry, notificationSlice } from "@store/slices/notification";

interface NotificationCardProps {
    notification: NotificationEntry;
}

const NotificationCard: FC<NotificationCardProps> = ({ notification }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [progress, setProgress] = useState(100);
    const [isHovering, setIsHovering] = useState(false);
    const animationFrameRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(Date.now());
    const endTimeRef = useRef<number | null>(null);
    const durationRef = useRef<number | null>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    // Konstante für die Verzögerung vor dem Ausblenden (Millisekunden)
    const CLOSE_DELAY = 450;

    const handleClose = useCallback(() => {
        // Alle Timer löschen
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        
        dispatch(notificationSlice.actions.closeNotification(notification.id));
    }, [dispatch, notification.id]);

    // Fortschrittsanimation starten und Timer für das Ausblenden einrichten
    const startProgressAnimation = useCallback((duration: number) => {
        if (!duration) return;
        
        // Zeiten für Animation und Timing setzen
        durationRef.current = duration;
        startTimeRef.current = Date.now();
        endTimeRef.current = startTimeRef.current + duration;
        
        // Progress-Update-Funktion definieren
        const updateProgress = () => {
            const now = Date.now();
            
            // Wenn die Zeit abgelaufen ist
            if (now >= (endTimeRef.current || 0)) {
                // Animation stoppen
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
                
                // Progress auf 0 setzen
                setProgress(0);
                
                // Notification nach kleiner Verzögerung ausblenden
                // Dies gibt dem Nutzer Zeit, die vollständig leere Progressbar zu sehen
                if (!closeTimeoutRef.current) {
                    closeTimeoutRef.current = setTimeout(() => {
                        handleClose();
                    }, CLOSE_DELAY);
                }
                
                return;
            }
            
            // Sonst Progress aktualisieren
            if (durationRef.current && endTimeRef.current) {
                const timeLeft = endTimeRef.current - now;
                const newProgress = Math.max(0, (timeLeft / durationRef.current) * 100);
                setProgress(newProgress);
                
                // Nur weitermachen, wenn wir nicht hovern
                if (!isHovering) {
                    animationFrameRef.current = requestAnimationFrame(updateProgress);
                }
            }
        };
        
        // Animation starten
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        
        // Vorhandene Close-Timeouts löschen
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        
        animationFrameRef.current = requestAnimationFrame(updateProgress);
    }, [handleClose, isHovering]);

    // Timer und Animation zurücksetzen
    const resetTimer = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        
        setProgress(100);
        
        if (notification.autoHideDuration && !isHovering) {
            startProgressAnimation(notification.autoHideDuration);
        }
    }, [notification.autoHideDuration, startProgressAnimation, isHovering]);

    // Mausevents verarbeiten
    const handleMouseEnter = useCallback(() => {
        setIsHovering(true);
        
        // Wenn ein Close-Timeout läuft, abbrechen
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        
        // Animation pausieren, aber Zeiten merken
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        
        // Zeitpunkt der Pause speichern
        if (durationRef.current && endTimeRef.current) {
            // Verbleibende Zeit berechnen und für späteren Neustart speichern
            const remainingTime = endTimeRef.current - Date.now();
            durationRef.current = remainingTime > 0 ? remainingTime : 0;
        }
    }, []);
    
    const handleMouseLeave = useCallback(() => {
        setIsHovering(false);
        
        // Wenn noch Zeit übrig ist, Timer neu starten
        if (durationRef.current && durationRef.current > 0) {
            startTimeRef.current = Date.now();
            endTimeRef.current = startTimeRef.current + durationRef.current;
            
            // Animation fortsetzen
            startProgressAnimation(durationRef.current);
        } else if (progress <= 0) {
            // Wenn der Progress bereits auf 0 ist und wir die Maus wegbewegen,
            // dann nach kurzer Verzögerung ausblenden
            if (!closeTimeoutRef.current) {
                closeTimeoutRef.current = setTimeout(() => {
                    handleClose();
                }, CLOSE_DELAY);
            }
        }
    }, [startProgressAnimation, progress, handleClose]);

    // Auto-hide notification after specified duration
    useEffect(() => {
        if (notification.autoHideDuration) {
            resetTimer();
        }
        
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, [notification.id, notification.autoHideDuration, resetTimer]);

    // Define colors based on notification type
    const getBorderColor = () => {
        switch (notification.type) {
            case "error":
                return theme.palette.semantic.notification.error;
            case "warning":
                return theme.palette.semantic.notification.warning;
            case "success":
                return theme.palette.semantic.notification.success;
            case "info":
            default:
                return theme.palette.semantic.notification.info;
        }
    };

    const getTextColor = () => {
        switch (notification.type) {
            case "error":
                return theme.palette.semantic.notification.error;
            case "warning":
            case "success":
            case "info":
            default:
                return theme.palette.semantic.text.paragraph;
        }
    };

    return (
        <Paper
            elevation={2}
            sx={{
                display: "flex",
                flexDirection: "column",
                mb: 1,
                p: 1.5,
                backgroundColor: "semantic.surface.secondary",
                border: `1px solid ${getBorderColor()}`,
                borderLeft: `4px solid ${getBorderColor()}`,
                position: "relative",
                width: "300px",
                borderRadius: 1,
                overflow: "hidden"
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {notification.autoHideDuration && (
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: getBorderColor()
                        },
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                    }}
                />
            )}
            
            <IconButton size="small" sx={{ position: "absolute", top: 5, right: 5 }} onClick={handleClose}>
                <CloseIcon fontSize="small" />
            </IconButton>

            {notification.title && (
                <Typography variant="subtitle2" fontWeight="bold" color={getTextColor()}>
                    {notification.title}
                </Typography>
            )}

            <Typography variant="body2" color={getTextColor()}>
                {notification.content}
            </Typography>
        </Paper>
    );
};

export default NotificationCard;
